'use client';
import React, { useState } from 'react';

import HotelBookerDatePicker from '@domain/hotel/searchBar/dateSelection/HotelBookerDatePicker';
import { resolveHotelSearchRangeOnApply } from '@domain/hotel/searchBar/dateSelection/resolveHotelSearchRangeOnApply';
import HotelSearchGuest from '@domain/hotel/searchBar/hotelSearchGuest/HotelSearchGuest';
import {
  DateType,
  HOTEL_SEARCH_DATE_FORMAT,
  formatMomentDate,
} from '@utils/dateUtil';
import useSearchBar from '@contexts/searchbar';
import { formatDate } from '@domain/hotel/searchBar/utils';
import clean from '@utils/cleanObject';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import STATIC_ROUTES from '@data/staticRoutes.json';
import { HotelSearchBarActionType } from '@contexts/searchbar/types/hotel';
import { Col } from '@components/formStyles';
import { useAppRouter } from '@contexts/router';
import areRoomsEqual from '@utils/compareRooms';
import { generateSearchRooms } from '@components/views/landing/components/booker/common/bookerUtils';

import {
  DateInputWrapper,
  SearchbarButton,
  GuestComponent,
  Label,
} from './styled';
import { useSetSelectedVariations } from '../../context';

const Searchbar = () => {
  const router = useAppRouter();
  const setSelectedVariations = useSetSelectedVariations();

  const {
    dispatch,
    state: { hotel: state },
  } = useSearchBar();

  const [localStartDate, setLocalStartDate] = useState(
    state.checkDates?.startDate || null
  );
  const [localEndDate, setLocalEndDate] = useState(
    state.checkDates?.endDate || null
  );

  const [localRooms, setLocalRooms] = useState<ListingSearchRoomInterface[]>(
    state.rooms
  );

  const [hasDatesError, setHasDatesError] = useState(false);

  const onDatesChange = (dates?: {
    startDate?: DateType | null;
    endDate?: DateType | null;
  }) => {
    setLocalStartDate(dates?.startDate as DateType);
    setLocalEndDate(dates?.endDate as DateType);
  };

  const checkFormState = () => {
    if (!localEndDate || !localStartDate) {
      setHasDatesError(true);
    }

    return localStartDate && localEndDate;
  };

  const handleSubmit = () => {
    const isValid = checkFormState();

    if (isValid) {
      const hotelUrl = state.location?.url;
      const inDate = formatDate(localStartDate);
      const outDate = formatDate(localEndDate);

      const rooms = generateSearchRooms(localRooms, true);

      const query = clean({
        in: inDate,
        out: outDate,
        room: rooms,
      });

      setSelectedVariations([]);

      router.push({
        pathname: `${STATIC_ROUTES.hotelPathPrefix}/${hotelUrl}`,
        query,
      });
    }
  };

  const isDatesDirty =
    formatMomentDate(localStartDate?.toDate(), HOTEL_SEARCH_DATE_FORMAT) !==
      formatMomentDate(
        state?.checkDates?.startDate,
        HOTEL_SEARCH_DATE_FORMAT
      ) ||
    formatMomentDate(localEndDate?.toDate(), HOTEL_SEARCH_DATE_FORMAT) !==
      formatMomentDate(state?.checkDates?.endDate, HOTEL_SEARCH_DATE_FORMAT);

  const isRoomsDirty = !areRoomsEqual(state.rooms, localRooms);

  const isFormDirty = !isDatesDirty && !isRoomsDirty;

  return (
    <DateInputWrapper>
      <Col>
        <GuestComponent>
          <Label>Gidiş & Dönüş Tarihleri</Label>
          <HotelBookerDatePicker
            dateChange={onDatesChange}
            setLocalEndDate={setLocalEndDate}
            setLocalStartDate={setLocalStartDate}
            localStartDate={localStartDate}
            localEndDate={localEndDate}
            onFocus={() => setHasDatesError(false)}
            onBlur={() => setHasDatesError(false)}
            isErrorDateSelection={hasDatesError}
            onDatepickerClose={() =>
              dispatch({
                type: HotelSearchBarActionType.SET_IS_HOTEL_DATE_SELECTED,
                payload: false,
              })
            }
            datepickerVisible={state.isHotelDateSelected}
            resolveRangeOnApply={resolveHotelSearchRangeOnApply}
          />
        </GuestComponent>
      </Col>
      <Col>
        <GuestComponent>
          <Label>Misafirler</Label>
          <HotelSearchGuest
            value={localRooms}
            onChange={(r) => setLocalRooms(r)}
          />
        </GuestComponent>
      </Col>
      <GuestComponent>
        <SearchbarButton
          disabled={isFormDirty}
          onClick={handleSubmit}
          type='secondary'
        >
          Ara
        </SearchbarButton>
      </GuestComponent>
    </DateInputWrapper>
  );
};

export default Searchbar;
